import React from 'react';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import axios from 'axios';
import { Typography } from '@material-ui/core';
import EnhancedTableHead from './EnhancedTableHead';
import SettingsModalContent from './Modal/SettingsModalContent';
import toRegionAbbrev from '../../utils/toRegionAbbrev';

function getSorting(order, orderBy) {
	if (
		orderBy === 'name' ||
		orderBy === 'address' ||
		orderBy === 'city' ||
		orderBy === 'province'
	) {
		return order === 'desc'
			? (a, b) => String(a[orderBy]).localeCompare(String(b[orderBy]))
			: (a, b) => String(b[orderBy]).localeCompare(String(a[orderBy]));
	}

	return order === 'desc'
		? (a, b) => b[orderBy] - a[orderBy]
		: (a, b) => a[orderBy] - b[orderBy];
}

const styles = (theme) => ({
	root: {
		width: '100%',
		marginTop: theme.spacing.unit * 3,
		marginBottom: theme.spacing.unit * 3,
		borderRadius: 12,
		boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
		overflow: 'hidden',
		backgroundColor: '#fff',
	},
	table: {
		minWidth: 1020,
		'& thead th': {
			fontWeight: 600,
			fontSize: '0.6875rem',
			color: '#64748b',
			backgroundColor: '#f8fafc',
			padding: '12px 16px',
			borderBottom: '2px solid #f3f4f6',
			textTransform: 'uppercase',
			letterSpacing: '0.03em',
			lineHeight: 1.1,
			whiteSpace: 'nowrap',
		},
		'& tbody td, & tbody th': {
			padding: '12px 16px',
			fontSize: '0.875rem',
			color: '#475569',
			borderBottom: '1px solid #f1f5f9',
			verticalAlign: 'middle',
		},
	},
	tableWrapper: {
		overflowX: 'auto',
	},
	bottomBar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '16px 24px',
		borderTop: '1px solid #f3f4f6',
		backgroundColor: '#f8fafc',
	},
	addButton: {
		backgroundColor: '#c655d6',
		color: '#fff',
		minWidth: '120px',
		textTransform: 'none',
		fontWeight: 500,
		padding: '8px 20px',
		borderRadius: '8px',
		boxShadow: 'none',
		'&:hover': {
			backgroundColor: '#b94bc8',
			boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
		},
	},
	button: {
		padding: '12px 32px',
		fontSize: '1rem',
		borderRadius: 4,
		textTransform: 'none',
		marginLeft: theme.spacing.unit * 2,
		marginTop: theme.spacing.unit * 1.5,
	},
	tableRow: {
		'&:hover': {
			backgroundColor: 'rgba(0, 0, 0, 0.04)',
		},
	},
	tableCell: {
		fontSize: '0.875rem',
	},
	noLocations: {
		padding: '40px 20px',
		textAlign: 'center',
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		padding: '32px 0',
	},
	header: {
		backgroundColor: '#f8fafc',
		padding: '24px 24px 20px',
		borderBottom: '1px solid #f3f4f6',
		display: 'flex',
		flexDirection: 'column',
	},
	headerTitle: {
		fontWeight: 400,
		fontSize: '14px',
		color: '#22223b',
		marginBottom: 4,
	},
	headerSubtitle: {
		fontWeight: 400,
		fontSize: '0.875rem',
		color: '#757575',
		marginTop: '-2px',
	},
});

class EnhancedTable extends React.Component {
	isInaccessibleLocation = (location) => {
		const status =
			location &&
			location.googleAccess &&
			location.googleAccess.status;
		const code =
			location &&
			location.googleAccess &&
			location.googleAccess.code;
		return (
			status === 'PERMISSION_DENIED' ||
			status === 'NOT_FOUND' ||
			code === 403 ||
			code === 404
		);
	};

	getProvinceHeaderLabel = (locations) => {
		const CANADA = new Set([
			'AB',
			'BC',
			'MB',
			'NB',
			'NL',
			'NS',
			'NT',
			'NU',
			'ON',
			'PE',
			'QC',
			'SK',
			'YT',
		]);
		const US = new Set([
			'AL',
			'AK',
			'AZ',
			'AR',
			'CA',
			'CO',
			'CT',
			'DE',
			'FL',
			'GA',
			'HI',
			'ID',
			'IL',
			'IN',
			'IA',
			'KS',
			'KY',
			'LA',
			'ME',
			'MD',
			'MA',
			'MI',
			'MN',
			'MS',
			'MO',
			'MT',
			'NE',
			'NV',
			'NH',
			'NJ',
			'NM',
			'NY',
			'NC',
			'ND',
			'OH',
			'OK',
			'OR',
			'PA',
			'RI',
			'SC',
			'SD',
			'TN',
			'TX',
			'UT',
			'VT',
			'VA',
			'WA',
			'WV',
			'WI',
			'WY',
			'DC',
		]);

		const codes = (Array.isArray(locations) ? locations : [])
			.map((l) => (l && l.province ? String(l.province).trim() : ''))
			.filter(Boolean)
			.map((p) => p.toUpperCase());

		if (!codes.length) return 'State/Province';

		const hasUSOnly = codes.some((c) => US.has(c) && !CANADA.has(c));
		const hasCanadaOnly = codes.some((c) => CANADA.has(c) && !US.has(c));

		if (hasUSOnly && !hasCanadaOnly) return 'State';
		if (hasCanadaOnly && !hasUSOnly) return 'Province';
		return 'State/Province';
	};

	constructor(props) {
		super(props);

		this.state = {
			order: 'asc',
			orderBy: 'address',
			selected: [],
			enterpriseLocations: [],
			hasAddressColumn: true,
			showInaccessible: false,
			page: 0,
			rowsPerPage: props.compact ? 5 : 25,
			loading: true,
			allSelected: false,
		};
	}

	async componentDidMount() {
		try {
			const res = await axios.get('/api/settings/locations');
			const { enterpriseLocations = [] } = res.data || {};

			// Only show locations if Google is connected
			const locations = this.props.auth.googleRefresh
				? enterpriseLocations
				: [];

			const hasAddressColumn = locations.some(
				(l) => Boolean(l && l.address && String(l.address).trim())
			);

			this.setState({
				enterpriseLocations: locations,
				numberOfLocations: locations.length,
				hasAddressColumn,
				orderBy:
					!hasAddressColumn && this.state.orderBy === 'address'
						? 'name'
						: this.state.orderBy,
				loading: false,
			});
		} catch (error) {
			console.error('Error loading locations:', error);
			this.setState({ loading: false, error: true });
		}
	}

	getTitle = () => {
		const { numberOfLocations } = this.state;
		return numberOfLocations
			? `You have ${numberOfLocations} location${
					numberOfLocations === 1 ? '' : 's'
			  }`
			: 'No locations found';
	};

	handleSelectAllClick = () => {
		const { allSelected } = this.state;
		if (!allSelected) {
			this.setState((state) => ({
				selected: state.enterpriseLocations.map((n) => n.id),
				allSelected: !allSelected,
			}));
			return;
		}
		this.setState({ selected: [], allSelected: !allSelected });
	};

	handleRequestSort = (event, property) => {
		if (!this.state.hasAddressColumn && property === 'address') {
			return;
		}
		const orderBy = property;
		let order = 'desc';

		if (this.state.orderBy === property && this.state.order === 'desc') {
			order = 'asc';
		}

		this.setState({ order, orderBy });
	};

	handleClick = (event, id, name) => {
		this.setState({
			open: true,
			_selectedEnterpriseLocation: id,
			selectedEnterpriseLocationName: name,
		});
	};

	handleChangePage = (event, page) => {
		this.setState({ page });
	};

	handleChangeRowsPerPage = (event) => {
		this.setState({ rowsPerPage: event.target.value });
	};

	handleClose = () => {
		this.setState({ open: false });
	};

	isSelected = (id) => this.state.selected.indexOf(id) !== -1;

	renderSkeletonRow = (rowIdx) => {
		const { hasAddressColumn } = this.state;
		const skeletonStyle = {
			background:
				'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
			borderRadius: 4,
			minHeight: 24,
			height: 24,
			animation: 'skeleton-loading 1.2s infinite',
			backgroundSize: '400% 100%',
			margin: 0,
		};
		// Using rowIdx as key is acceptable for loading skeletons
		// eslint-disable-next-line react/no-array-index-key
		return (
			<TableRow key={`skeleton-${rowIdx}`}>
				{[...Array(hasAddressColumn ? 3 : 2)].map((_, colIdx) => (
					// eslint-disable-next-line react/no-array-index-key
					<TableCell key={`skeleton-cell-${rowIdx}-${colIdx}`}>
						<div style={skeletonStyle} />
					</TableCell>
				))}
			</TableRow>
		);
	};

	renderNoLocations = () => (
		<Paper className={this.props.classes.root}>
			<div className={this.props.classes.header}>
				<Typography variant="h6" className={this.props.classes.headerTitle}>
					Connect your business account
				</Typography>
				<Typography className={this.props.classes.headerSubtitle}>
					Connect Google to manage locations and business settings.
				</Typography>
			</div>
			<div
				style={{
					padding: '24px',
					textAlign: 'center',
				}}
			>
				<Button
					variant="contained"
					disableElevation
					href="/auth/google"
					style={{
						backgroundColor: '#c655d6',
						color: '#fff',
						textTransform: 'none',
						padding: '10px 18px',
						borderRadius: 10,
						fontWeight: 700,
					}}
				>
					Connect Google Account
				</Button>
			</div>
		</Paper>
	);

	render() {
		const { classes } = this.props;
		const {
			enterpriseLocations,
			order,
			orderBy,
			selected,
			rowsPerPage,
			page,
			loading,
			allSelected,
			_selectedEnterpriseLocation,
			selectedEnterpriseLocationName,
			error,
			hasAddressColumn,
			showInaccessible,
		} = this.state;
		const provinceLabel = this.getProvinceHeaderLabel(enterpriseLocations);
		const inaccessibleCount = (enterpriseLocations || []).filter((l) =>
			this.isInaccessibleLocation(l)
		).length;
		const accessibleLocations = (enterpriseLocations || []).filter(
			(l) => !this.isInaccessibleLocation(l)
		);
		const inaccessibleLocations = (enterpriseLocations || []).filter((l) =>
			this.isInaccessibleLocation(l)
		);
		const sortFn = getSorting(order, orderBy);
		const visibleLocations = showInaccessible
			? [
					...accessibleLocations.slice().sort(sortFn),
					...inaccessibleLocations.slice().sort(sortFn),
			  ]
			: accessibleLocations.slice().sort(sortFn);

		if (loading) {
			return (
				<Paper className={classes.root}>
					<div className={classes.header}>
						<Typography
							variant='h6'
							className={classes.headerTitle}
						>
							Saved Locations
						</Typography>
						<Typography
							className={`${classes.headerSubtitle} subtitle`}
						>
							{this.state.numberOfLocations
								? `You have ${
										this.state.numberOfLocations
								  } location${
										this.state.numberOfLocations === 1
											? ''
											: 's'
								  }`
								: 'No locations found'}
						</Typography>
					</div>
					<div className={classes.tableWrapper}>
						<Table
							className={classes.table}
							aria-labelledby='tableTitle'
						>
							<EnhancedTableHead
								numSelected={selected.length}
								order={order}
								orderBy={orderBy}
								onRequestSort={this.handleRequestSort}
								onSelectAllClick={this.handleSelectAllClick}
								rowCount={rowsPerPage}
								allSelected={allSelected}
								hideAddressColumn={!hasAddressColumn}
								provinceLabel={provinceLabel}
							/>
							<TableBody>
								{Array.from({ length: rowsPerPage }).map((_, idx) =>
									this.renderSkeletonRow(idx)
								)}
							</TableBody>
						</Table>
					</div>
					<div className={classes.bottomBar}>
						<Button className={classes.addButton} disabled>
							Add Locations
						</Button>
						<TablePagination
							component='div'
							count={0}
							rowsPerPage={rowsPerPage}
							rowsPerPageOptions={[5, 10, 25]}
							page={page}
							onChangePage={this.handleChangePage}
							onChangeRowsPerPage={this.handleChangeRowsPerPage}
						/>
					</div>
				</Paper>
			);
		}

		if (error || !this.props.auth.googleRefresh) {
			return this.renderNoLocations();
		}

		return (
			<div>
				<Paper className={classes.root}>
					<div className={classes.header}>
						<Typography
							variant='h6'
							className={classes.headerTitle}
						>
							Saved Locations
						</Typography>
						<Typography
							className={`${classes.headerSubtitle} subtitle`}
						>
							{enterpriseLocations.length
								? `You have ${
										enterpriseLocations.length
								  } location${
										enterpriseLocations.length === 1
											? ''
											: 's'
								  }`
								: 'No locations found'}
						</Typography>
					</div>
					<div className={classes.tableWrapper}>
						<Table
							className={classes.table}
							aria-labelledby='tableTitle'
						>
							<EnhancedTableHead
								numSelected={selected.length}
								order={order}
								orderBy={orderBy}
								onRequestSort={this.handleRequestSort}
								onSelectAllClick={this.handleSelectAllClick}
								rowCount={visibleLocations.length}
								allSelected={allSelected}
								hideAddressColumn={!hasAddressColumn}
								provinceLabel={provinceLabel}
							/>
							<TableBody>
								{visibleLocations
									.slice(
										page * rowsPerPage,
										page * rowsPerPage + rowsPerPage
									)
									.map((n) => {
										const isSelected = this.isSelected(
											n._id
										);
										return (
											<TableRow
												hover
												role='checkbox'
												aria-checked={isSelected}
												tabIndex={-1}
												key={n._id}
												selected={isSelected}
											>
												<TableCell
													component='th'
													scope='row'
													className={
														classes.tableCell
													}
												>
													{n.name}
													{this.isInaccessibleLocation(
														n
													) ? (
														<span
															style={{
																marginLeft: 10,
																padding:
																	'2px 8px',
																borderRadius: 999,
																border: '1px solid #e5e7eb',
																backgroundColor:
																	'#f8fafc',
																color: '#64748b',
																fontSize:
																	'0.75rem',
																fontWeight: 500,
																whiteSpace:
																	'nowrap',
															}}
														>
															No access
														</span>
													) : null}
												</TableCell>
												{hasAddressColumn ? (
													<TableCell
														className={
															classes.tableCell
														}
													>
														{this.isInaccessibleLocation(
															n
														)
															? 'No access'
															: n.address}
													</TableCell>
												) : null}
												<TableCell
													className={
														classes.tableCell
													}
												>
													{toRegionAbbrev(n.province)}
												</TableCell>
											</TableRow>
										);
									})}
							</TableBody>
						</Table>
					</div>
					{!loading && inaccessibleCount > 0 ? (
						<div
							style={{
								padding: '10px 16px',
								borderTop: '1px solid #f3f4f6',
								borderBottom: '1px solid #f3f4f6',
								backgroundColor: '#fff',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 12,
								flexWrap: 'wrap',
							}}
						>
							<Typography
								style={{
									fontSize: '0.9rem',
									color: '#5f6368',
								}}
							>
								{showInaccessible
									? `Showing ${inaccessibleCount} location${
											inaccessibleCount === 1 ? '' : 's'
									  } that Chekkit no longer has access to.`
									: `Hiding ${inaccessibleCount} location${
											inaccessibleCount === 1 ? '' : 's'
									  } that Chekkit no longer has access to.`}
							</Typography>
							<Button
								variant='contained'
								size='small'
								disableElevation
								style={{
									backgroundColor: '#c655d6',
									color: '#fff',
									textTransform: 'none',
									fontWeight: 500,
								}}
								onClick={() =>
									this.setState({
										showInaccessible: !showInaccessible,
										page: 0,
									})
								}
							>
								{showInaccessible ? 'Hide' : 'Show'}
							</Button>
						</div>
					) : null}
					<div className={classes.bottomBar}>
						<Button className={classes.addButton}>
							Add Locations
						</Button>
						<TablePagination
							component='div'
							count={visibleLocations.length}
							rowsPerPage={rowsPerPage}
							rowsPerPageOptions={[5, 10, 25]}
							page={page}
							onChangePage={this.handleChangePage}
							onChangeRowsPerPage={this.handleChangeRowsPerPage}
						/>
					</div>
				</Paper>

				<Dialog
					open={this.state.open}
					keepMounted
					maxWidth={false}
					scroll='paper'
					onClose={this.handleClose}
					aria-labelledby='alert-dialog-slide-title'
					aria-describedby='alert-dialog-slide-description'
					PaperProps={{
						style: { minWidth: 900, minHeight: 500 },
					}}
				>
					<DialogTitle id='alert-dialog-slide-title'>
						{selectedEnterpriseLocationName || 'Latest Reviews'}
					</DialogTitle>
					<DialogContent>
						<SettingsModalContent
							_enterpriseLocation={_selectedEnterpriseLocation}
						/>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={this.handleClose}
							color='primary'
							className={classes.button}
						>
							Close
						</Button>
					</DialogActions>
				</Dialog>
			</div>
		);
	}
}

function mapStateToProps(state) {
	return { error: state.error, auth: state.auth };
}

export default connect(mapStateToProps)(withStyles(styles)(EnhancedTable));



// WEBPACK FOOTER //
// ./src/components/settings/SettingsTable.js